import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {BicDropdownElementComponent, BicSelectComponent} from '@buildinginformationcloud/bic-ui-components';
import {JsonSchema, SelectOption} from '../schema-form.types';
import {SchemaSourceService} from '../schema-source.service';
import {enumLabel} from '../widget-registry';

/**
 * array + (x-source | items.enum) → multi-select, mirroring the Regelset pattern
 * (bic-select [multiple] + bic-dropdown-element [selection]). Value = array of option
 * values (e.g. document-class ids).
 */
@Component({
  selector: 'sf-multiselect',
  standalone: true,
  imports: [BicSelectComponent, BicDropdownElementComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <bic-select
      [multiple]="true"
      [value]="selected"
      [placeholder]="placeholder"
      (valueChange)="onChange($event)">
      @for (opt of options; track opt.value) {
        <bic-dropdown-element [selection]="true" [value]="opt.value">{{ opt.label }}</bic-dropdown-element>
      }
    </bic-select>
  `,
})
export class SfMultiselectComponent implements OnInit {
  @Input() schema: JsonSchema | null = null;
  @Input() value: any;
  @Output() valueChange = new EventEmitter<any>();

  options: SelectOption[] = [];
  private readonly sources = inject(SchemaSourceService);

  get selected(): any[] {
    return Array.isArray(this.value) ? this.value : [];
  }

  get placeholder(): string {
    return this.schema?.['x-placeholder'] ?? 'Auswählen…';
  }

  ngOnInit(): void {
    const source = this.schema?.['x-source'];
    const enumVals = this.schema?.items?.enum;
    if (source) {
      this.sources.getOptions(source).subscribe(opts => (this.options = opts));
    } else if (Array.isArray(enumVals)) {
      // Beschriftung über `x-enumLabels` – am `items`-Schema (dort steht das enum) oder am
      // Feld selbst. Ohne das zeigte die Mehrfachauswahl den rohen Enum-Wert; bei technischen
      // Schlüsseln wie Regel-IDs ist das für den Nutzer nicht lesbar.
      const labelSource = this.schema?.items?.['x-enumLabels'] ? this.schema.items : this.schema;
      this.options = enumVals.map(v => ({value: v, label: enumLabel(labelSource, v)}));
    }
  }

  onChange(value: any): void {
    this.valueChange.emit(Array.isArray(value) ? value : value == null ? [] : [value]);
  }
}
